import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useI18n } from '../../contexts/I18nContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { Checkbox } from '../../components/ui/checkbox';
import { Mail, Lock, User, AlertCircle, Loader2, Building2, TrendingUp, UserCheck, Shield } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_1c07591d-63e3-4991-946f-f8a8e8c125c9/artifacts/i94v1fmk_IMG_0910.jpeg";

const RegisterPage = () => {
  const { register } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: 'investor',
    referralType: '',
    referralName: '',
    referralDetails: '',
    acceptTerms: false,
    acceptRisks: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (formData.password !== formData.confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }
    
    if (formData.password.length < 8) {
      setError('La contraseña debe tener al menos 8 caracteres');
      return;
    }
    
    if (!formData.acceptTerms || !formData.acceptRisks) {
      setError('Debe aceptar los términos y reconocer los riesgos');
      return;
    }

    if (!formData.referralType) {
      setError('Debe indicar cómo conoció Silver Brick Development');
      return;
    }

    if (!formData.referralName.trim()) {
      setError('Debe proporcionar el nombre de quien lo refirió o del ejecutivo que lo contactó');
      return;
    }
    
    setLoading(true);
    
    try {
      await register({
        name: formData.name,
        email: formData.email,
        password: formData.password,
        role: formData.role,
        referral_type: formData.referralType,
        referral_name: formData.referralName,
        referral_details: formData.referralDetails,
      });
      navigate('/app/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al crear la cuenta');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left Side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url(https://images.unsplash.com/photo-1579283987703-5ae978f95ace?q=80&w=2574&auto=format&fit=crop)`
          }}
        >
          <div className="absolute inset-0 bg-background/80" />
        </div>
        <div className="absolute inset-0 flex items-center justify-center p-16">
          <div className="max-w-lg text-center">
            <h2 className="heading-2 mb-6">
              Únase a Nuestra Red de Inversionistas
            </h2>
            <p className="body-text">
              Acceda a oportunidades de inversión inmobiliaria de alta calidad con total transparencia.
            </p>
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 overflow-y-auto">
        <div className="w-full max-w-md py-8">
          <div className="mb-8">
            <Link to="/">
              <img src={LOGO_URL} alt="Silver Brick Development" className="h-16 mb-8" />
            </Link>
            <h1 className="heading-2 mb-2">{t('auth.register')}</h1>
            <p className="body-text">
              Complete el formulario para crear su cuenta
            </p>
          </div>

          {error && (
            <Alert variant="destructive" className="mb-6">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Account Type */}
            <div className="space-y-3">
              <Label>{t('auth.registerAs')}</Label>
              <RadioGroup
                value={formData.role}
                onValueChange={(value) => setFormData(prev => ({ ...prev, role: value }))}
                className="grid grid-cols-2 gap-4"
              >
                <Label
                  htmlFor="investor"
                  className={`flex flex-col items-center gap-2 p-4 border rounded-sm cursor-pointer transition-colors ${
                    formData.role === 'investor' 
                      ? 'border-primary bg-primary/5' 
                      : 'border-border hover:border-primary/50'
                  }`}
                >
                  <RadioGroupItem value="investor" id="investor" className="sr-only" />
                  <TrendingUp className="h-6 w-6" strokeWidth={1.5} />
                  <span className="text-sm font-medium">{t('auth.investor')}</span>
                </Label>
                <Label
                  htmlFor="property_contributor"
                  className={`flex flex-col items-center gap-2 p-4 border rounded-sm cursor-pointer transition-colors ${
                    formData.role === 'property_contributor' 
                      ? 'border-primary bg-primary/5' 
                      : 'border-border hover:border-primary/50'
                  }`}
                >
                  <RadioGroupItem value="property_contributor" id="property_contributor" className="sr-only" />
                  <Building2 className="h-6 w-6" strokeWidth={1.5} />
                  <span className="text-sm font-medium">{t('auth.propertyContributor')}</span>
                </Label>
              </RadioGroup>
            </div>

            <div className="space-y-2">
              <Label htmlFor="name">{t('auth.name')}</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                <Input
                  id="name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Juan Pérez"
                  className="pl-10"
                  required
                  data-testid="register-name"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">{t('auth.email')}</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="nombre@empresa.com"
                  className="pl-10"
                  required
                  data-testid="register-email"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password">{t('auth.password')}</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                <Input
                  id="password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                  className="pl-10"
                  required
                  data-testid="register-password"
                />
              </div>
              <p className="text-xs text-muted-foreground">Mínimo 8 caracteres</p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">{t('auth.confirmPassword')}</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="••••••••"
                  className="pl-10"
                  required
                  data-testid="register-confirm-password"
                />
              </div>
            </div>

            {/* Referral Section - Closed Fund Notice */}
            <div className="space-y-4 p-4 border border-primary/20 rounded-sm bg-primary/5">
              <div className="flex items-start gap-3">
                <Shield className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                <div>
                  <p className="text-sm font-medium text-foreground mb-1">
                    Fondo de Inversión Exclusivo
                  </p>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    Silver Brick Development es un fondo cerrado exclusivo para inversionistas referidos por nuestra red de clientes 
                    o contactados directamente por nuestros ejecutivos premium. Por favor, indique cómo llegó a nosotros.
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="referralType">¿Cómo conoció Silver Brick? <span className="text-red-500">*</span></Label>
                <Select
                  value={formData.referralType}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, referralType: value }))}
                >
                  <SelectTrigger data-testid="referral-type-select">
                    <SelectValue placeholder="Seleccione una opción" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="executive_contact">Un ejecutivo de Silver Brick me contactó</SelectItem>
                    <SelectItem value="client_referral">Recomendación de un cliente/inversionista</SelectItem>
                    <SelectItem value="personal_referral">Recomendación personal (amigo, familiar, colega)</SelectItem>
                    <SelectItem value="professional_network">Red profesional o asociación empresarial</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="referralName">
                  {formData.referralType === 'executive_contact' 
                    ? 'Nombre del ejecutivo que lo contactó' 
                    : 'Nombre de quien lo refirió'} <span className="text-red-500">*</span>
                </Label>
                <div className="relative">
                  <UserCheck className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                  <Input
                    id="referralName"
                    name="referralName"
                    type="text"
                    value={formData.referralName}
                    onChange={handleChange}
                    placeholder={formData.referralType === 'executive_contact' 
                      ? "Ej: Carlos Mendoza" 
                      : "Nombre completo de quien lo refirió"}
                    className="pl-10"
                    required
                    data-testid="referral-name"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="referralDetails">Información adicional (opcional)</Label>
                <Textarea
                  id="referralDetails"
                  name="referralDetails"
                  value={formData.referralDetails}
                  onChange={handleChange}
                  placeholder="Proporcione cualquier detalle adicional que nos ayude a identificar su referencia (empresa, fecha de contacto, etc.)"
                  className="min-h-[80px] text-sm"
                  data-testid="referral-details"
                />
              </div>
            </div>

            {/* Checkboxes */}
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <Checkbox
                  id="acceptTerms"
                  checked={formData.acceptTerms}
                  onCheckedChange={(checked) => 
                    setFormData(prev => ({ ...prev, acceptTerms: checked }))
                  }
                  data-testid="accept-terms"
                />
                <label htmlFor="acceptTerms" className="text-sm text-muted-foreground leading-relaxed">
                  Acepto los{' '}
                  <Link to="/legal/terms" className="text-foreground hover:underline" target="_blank">
                    Términos y Condiciones
                  </Link>{' '}
                  y la{' '}
                  <Link to="/legal/privacy" className="text-foreground hover:underline" target="_blank">
                    Política de Privacidad
                  </Link>
                </label>
              </div>
              
              <div className="flex items-start space-x-3">
                <Checkbox
                  id="acceptRisks"
                  checked={formData.acceptRisks}
                  onCheckedChange={(checked) => 
                    setFormData(prev => ({ ...prev, acceptRisks: checked }))
                  }
                  data-testid="accept-risks"
                />
                <label htmlFor="acceptRisks" className="text-sm text-muted-foreground leading-relaxed">
                  Reconozco que la inversión en bienes raíces conlleva riesgos, incluyendo la posible pérdida de capital, y que los rendimientos proyectados no están garantizados.
                </label>
              </div>
            </div>
            
            <Button 
              type="submit" 
              className="w-full btn-primary"
              disabled={loading}
              data-testid="register-submit"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                t('auth.register')
              )}
            </Button>
          </form>

          <div className="mt-8">
          </div>

          <p className="mt-8 text-center text-sm text-muted-foreground">
            {t('auth.hasAccount')}{' '}
            <Link to="/login" className="text-foreground hover:underline">
              {t('auth.login')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
